import { IntlShape } from 'react-intl';
import {
  Feature,
  getFeatureByAddOn,
  OrderItem,
  RecurlyAddOn,
} from '../../../../models';
import { getFreeCrossBrowserSessionQuota } from './trueBundlePreview';

/**
 * Presentation of the components of a True Bundle Starter (PIVOT 2026-09-28): True Manual Starter
 * = 3 x `true_platform` + 1 x `testcloud_session_livetesting`, True Automation Starter =
 * 3 x `true_automation`. The package contents always come from LaunchDarkly `trueBundleConfig`
 * (card) or from the BE preview / order / subscription (everywhere else); this file only knows how
 * to NAME a component and which of them are True seats.
 */

/** Add-ons counted as True seats (BE `trueBundleSeatAddOns`): they earn free TestCloud sessions. */
export const TRUE_BUNDLE_SEAT_ADD_ONS: string[] = [
  RecurlyAddOn.TRUE_PLATFORM_ONLY,
  RecurlyAddOn.TRUE_AUTOMATION,
];

export const isTrueBundleSeatAddOn = (addOnCode: string): boolean => (
  TRUE_BUNDLE_SEAT_ADD_ONS.includes(addOnCode)
);

/** Total True seats of a bundle, from its [addOnCode, quantity] components. */
export const getTrueBundleSeatCount = (
  components: Array<[string, number]>,
): number => components
  .filter(([addOnCode]) => isTrueBundleSeatAddOn(addOnCode))
  .reduce((sum, [, quantity]) => sum + Math.max(quantity, 0), 0);

/**
 * TestCloud Cross-Browser sessions the bundle gets for FREE (requirement 2026-09-29): the BE
 * grants them at provisioning from the bundle's seats, exactly like a standalone True Automation /
 * True Platform purchase. They are not in `trueBundleConfig`, so the card always computes them
 * here. Everywhere else the BE is now the source of truth - a `testcloud_session_crossbrowser_free`
 * line in the preview (Order Summary) and a `BUNDLE_COMPONENT` row with that plan id in the order
 * (Order History, Payment Status) - and this helper is only the FALLBACK for what the BE does not
 * carry: a preview from a BE deployed before that change, and orders placed before it (see
 * [getFallbackFreeCrossBrowserSessions]). A pre-pivot bundle has no True seats, so it gets 0.
 *
 * The 3-seat Starters get 1. The count comes from [getFreeCrossBrowserSessionQuota], which
 * HARDCODES a FLAT step of 5. The BE reads the step from the LD flag `truePlatformConfig` (FLAT
 * `stepConfig` or STAIR_STEP): if ops change that flag, `FREE_CROSS_BROWSER_SESSION_SEAT_STEP` in
 * trueBundlePreview.ts must change with it, or the free TC-CB line on the card / Order Summary
 * shows a different number from what the BE actually grants.
 */
export const getTrueBundleFreeCrossBrowserSessions = (
  components: Array<[string, number]>,
): number => getFreeCrossBrowserSessionQuota(getTrueBundleSeatCount(components));

/** The add-on code of the FREE TestCloud Cross-Browser sessions a bundle's seats earn. */
export const isFreeCrossBrowserAddOn = (addOnCode: string | undefined): boolean => (
  addOnCode === RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER_FREE
);

/**
 * Whether the order already stores the free Cross-Browser grant of a bundle as one of its
 * component rows (orders placed once the BE writes it). Used to never draw the fallback line on
 * top of the stored one.
 */
export const hasFreeCrossBrowserComponentRow = (
  items: Array<Pick<OrderItem, 'type' | 'planId'>>,
): boolean => items.some(item => item.type === 'BUNDLE_COMPONENT'
  && isFreeCrossBrowserAddOn(item.planId));

/**
 * The components of a bundle order row, with the free Cross-Browser row(s) moved to the END
 * (Decision 11) - after the real components, whatever order the payload lists them in.
 */
export const sortFreeCrossBrowserComponentsLast = <T extends Pick<OrderItem, 'planId'>>(
  components: T[],
): T[] => {
  const realComponents = components
    .filter(component => !isFreeCrossBrowserAddOn(component.planId));
  const freeComponents = components
    .filter(component => isFreeCrossBrowserAddOn(component.planId));
  return [...realComponents, ...freeComponents];
};

/**
 * Free Cross-Browser sessions Order History / Payment Status must draw THEMSELVES under a bundle
 * row: 0 once the order stores the grant as a component row (the row is rendered instead), else
 * the helper estimate from the stored seat components - an order placed before the BE wrote that
 * row. A pre-pivot bundle (no True seats) gets 0, so nothing is ever drawn twice.
 */
export const getFallbackFreeCrossBrowserSessions = (
  components: Array<Pick<OrderItem, 'type' | 'planId' | 'quantity'>>,
): number => (hasFreeCrossBrowserComponentRow(components)
  ? 0
  : getTrueBundleFreeCrossBrowserSessions(
    components.map(component => [component.planId ?? '', component.quantity]),
  ));

// `in` rather than Object.hasOwn: the build targets ES5 with no core-js polyfill and browserslist
// ">0.2%, not dead" still includes browsers without Object.hasOwn (Safari < 15.4, Chrome < 93),
// where it would throw. `in` also sees inherited keys, which is harmless here: every id passed in
// is a dotted message key (`page.plan.v2...`) and can never match an Object.prototype member.
const hasMessage = (intl: IntlShape, id: string): boolean => id in intl.messages;

/**
 * Component name used inside a bundle ("TestOps (True Manual)", "TestCloud Live Testing"). The
 * seat names carry the products the seat unlocks on EVERY surface (requirement 2026-09-30). The
 * shared `addon.<code>.display.name` keys stay as they are - the legacy True Team rows read
 * "Standard" / "Live Testing" from them - so the bundle wording lives under
 * `bundle.component.<code>.name` and falls back to the shared key (then to the BE name) for any
 * other code.
 */
export const formatTrueBundleComponentName = (
  intl: IntlShape,
  addOnCode: string,
  fallbackName?: string,
): string => {
  const bundleNameId = `bundle.component.${addOnCode}.name`;
  if (hasMessage(intl, bundleNameId)) {
    return intl.formatMessage({ id: bundleNameId });
  }
  const addOnNameId = `addon.${addOnCode}.display.name`;
  if (hasMessage(intl, addOnNameId)) {
    return intl.formatMessage({ id: addOnNameId });
  }
  return fallbackName || addOnCode;
};

/**
 * Order Summary line of a component: "3 × TestOps (True Manual)", "1 × TestCloud Live Testing".
 * The seat wording — the unlocked products first, the seat name in brackets — lives INSIDE
 * `bundle.component.<code>.name` (it shows on every surface, not just here); the
 * `bundle.component.<code>.summary.suffix` mechanism stays for any future summary-only wording.
 */
export const formatTrueBundleSummaryComponent = (
  intl: IntlShape,
  addOnCode: string,
  quantity: number,
  fallbackName?: string,
): string => {
  const name = formatTrueBundleComponentName(intl, addOnCode, fallbackName);
  const suffixId = `bundle.component.${addOnCode}.summary.suffix`;
  return hasMessage(intl, suffixId)
    ? intl.formatMessage(
      { id: 'page.plan.v2.order.summary.bundle.component.with.suffix' },
      { quantity, name, suffix: intl.formatMessage({ id: suffixId }) },
    )
    : intl.formatMessage(
      { id: 'page.plan.v2.order.summary.bundle.component' },
      { quantity, name },
    );
};

export type TrueBundleChipIcon = 'studio' | 'cloud' | 'testmgmt';

export interface TrueBundleCardChip {
  key: string;
  quantity: number;
  name: string;
  iconKey: TrueBundleChipIcon;
}

type ChipTemplate = { key: string; labelId: string; iconKey: TrueBundleChipIcon };

const TESTOPS_CHIP: ChipTemplate = {
  key: 'testops',
  labelId: 'page.plan.v2.bundle.card.component.testops',
  iconKey: 'testmgmt',
};
const CROSS_BROWSER_CHIP: ChipTemplate = {
  key: RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER,
  labelId: 'page.plan.v2.bundle.card.component.testcloud_session_crossbrowser',
  iconKey: 'cloud',
};

/**
 * How the card (design 23.9) spells an add-on: by the PRODUCTS it unlocks. A True Platform seat
 * is "TestOps"; a True Automation seat is "Katalon Studio" + "TestOps".
 */
const CARD_CHIPS_BY_ADD_ON: Record<string, ChipTemplate[]> = {
  [RecurlyAddOn.TRUE_PLATFORM_ONLY]: [TESTOPS_CHIP],
  [RecurlyAddOn.TRUE_AUTOMATION]: [
    {
      key: 'studio',
      labelId: 'page.plan.v2.bundle.card.component.studio',
      iconKey: 'studio',
    },
    TESTOPS_CHIP,
  ],
  [RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER]: [CROSS_BROWSER_CHIP],
  [RecurlyAddOn.TESTCLOUD_SESSION_LIVETESTING]: [{
    key: RecurlyAddOn.TESTCLOUD_SESSION_LIVETESTING,
    labelId: 'page.plan.v2.bundle.card.component.testcloud_session_livetesting',
    iconKey: 'cloud',
  }],
};

const TESTCLOUD_FEATURES: ReadonlySet<Feature> = new Set<Feature>([
  Feature.TESTCLOUD_SESSION_CROSS_BROWSER,
  Feature.TESTCLOUD_SESSION_LIVE_TESTING,
  Feature.TESTCLOUD_SESSION_MOBILE_APP,
  Feature.TESTCLOUD_SESSION_PRIVATE_DEVICE,
  Feature.TESTCLOUD_SESSION_WEB_DESKTOP,
]);

const toChips = (
  intl: IntlShape,
  addOnCode: string,
  quantity: number,
): TrueBundleCardChip[] => {
  const templates = CARD_CHIPS_BY_ADD_ON[addOnCode];
  if (templates) {
    return templates.map(template => ({
      key: `${addOnCode}:${template.key}`,
      quantity,
      name: intl.formatMessage({ id: template.labelId }),
      iconKey: template.iconKey,
    }));
  }
  // An add-on the card has no wording for still shows up, under its component name.
  return [{
    key: addOnCode,
    quantity,
    name: formatTrueBundleComponentName(intl, addOnCode),
    iconKey: TESTCLOUD_FEATURES.has(getFeatureByAddOn(addOnCode)) ? 'cloud' : 'testmgmt',
  }];
};

/**
 * Chips of a Starter card, built from the `trueBundleConfig` components: the seat chips first,
 * then the FREE TestCloud Cross-Browser chip (not in the config, see
 * [getTrueBundleFreeCrossBrowserSessions]), then the other add-ons - the order of design 23.9
 * (Manual: TestOps, Cross Browser, Live Testing; Automation: Katalon Studio, TestOps, Cross
 * Browser).
 */
export const getTrueBundleCardChips = (
  intl: IntlShape,
  components: Array<[string, number]>,
): TrueBundleCardChip[] => {
  const seatChips = components
    .filter(([addOnCode]) => isTrueBundleSeatAddOn(addOnCode))
    .flatMap(([addOnCode, quantity]) => toChips(intl, addOnCode, quantity));
  const freeCrossBrowser = getTrueBundleFreeCrossBrowserSessions(components);
  const freeChips: TrueBundleCardChip[] = freeCrossBrowser > 0
    ? [{
      key: 'free:testcloud_session_crossbrowser',
      quantity: freeCrossBrowser,
      name: intl.formatMessage({ id: CROSS_BROWSER_CHIP.labelId }),
      iconKey: CROSS_BROWSER_CHIP.iconKey,
    }]
    : [];
  const otherChips = components
    .filter(([addOnCode]) => !isTrueBundleSeatAddOn(addOnCode))
    .flatMap(([addOnCode, quantity]) => toChips(intl, addOnCode, quantity));
  return [...seatChips, ...freeChips, ...otherChips];
};

export interface TrueBundleCardPrice {
  /** What the package costs per year once the Starter campaign applies. */
  annualPrice: number;
  /** `annualPrice / 12`, rounded to whole dollars ($1,499 -> $125, $4,099 -> $342). */
  monthlyPrice: number;
  /** `1 - annualPrice / listPrice` in whole percent; 0 when there is nothing to advertise. */
  savingPercent: number;
}

/**
 * Card price of a Starter. `ecomSubtotal` is the pre-campaign package price (Σ add-ons at eCom
 * prices - what the BE charges before the campaign), `campaignDiscount` the FIXED amount of the
 * campaign that discounts THIS bundle, `publishedOfferPrice` the LD offer price used only while
 * the campaigns are still loading, and `listPrice` the display-only list price behind the badge.
 */
export const getTrueBundleCardPrice = ({
  ecomSubtotal,
  campaignDiscount,
  publishedOfferPrice,
  listPrice,
}: {
  ecomSubtotal: number;
  campaignDiscount?: number;
  publishedOfferPrice?: number;
  listPrice?: number;
}): TrueBundleCardPrice => {
  let annualPrice = ecomSubtotal;
  if (campaignDiscount !== undefined && campaignDiscount > 0) {
    annualPrice = Math.max(ecomSubtotal - campaignDiscount, 0);
  } else if (
    publishedOfferPrice !== undefined
    && publishedOfferPrice > 0
    && publishedOfferPrice < ecomSubtotal
  ) {
    annualPrice = publishedOfferPrice;
  }
  const savingPercent = listPrice && listPrice > annualPrice
    ? Math.round((1 - annualPrice / listPrice) * 100)
    : 0;
  return {
    annualPrice,
    monthlyPrice: Math.round(annualPrice / 12),
    savingPercent,
  };
};

// Float noise guard for the "round up" helpers below: 45% computed as 45.0000000001 must stay 45.
const ROUND_UP_EPSILON = 1e-9;

/**
 * Monthly equivalent of an annual Starter price on the "Choose your plan" cards, ROUNDED UP so the
 * card never understates what the customer pays ($1,000 / yr -> $84 / month, $1,800 -> $150).
 */
export const getStarterMonthlyEquivalent = (annualPrice: number): number => Math.max(
  Math.ceil(annualPrice / 12 - ROUND_UP_EPSILON),
  0,
);

/**
 * "Up to N% off" of the Starter banner: the best saving badge among the cards on screen (each
 * already rounded, e.g. Katalon Studio 1 - 1,000 / 2,199 = 54.5% -> 55), so the banner never
 * claims a percentage no card shows. 0 = nothing to advertise.
 */
export const getStarterBannerSavingPercent = (cardPercents: number[]): number => Math.max(
  0,
  ...cardPercents,
);
